import {
    ChangeEvent,
    FC,
    ReactElement,
    useEffect,
    useState,
} from 'react'
import { useParams } from 'react-router'
import classNames from 'classnames'

import { convertDateToDateYearCompleteString } from '@/helpers/DateTimeParser'
import { trans } from '@/helpers/trans'
import { FeedbackCategoryResource } from '@/models'
import { getFeedback, getFeedbackCategories, submitFeedback } from '@/redux/feedback/feedbackActions'
import { useTypedDispatch, useTypedSelector } from '@/redux/store'

import './FeedbackPage.scss'

const getCategoryName = (name: string): string => trans(`pages.feedback.${name.toLowerCase()}`) || name

export const FeedbackPage: FC = (): ReactElement => {
    const dispatch = useTypedDispatch()
    const { feedbackUuid } = useParams()
    const {
        categories,
        name,
        date,
        failure,
        loading,
        sendingData,
        dataSubmitSuccess,
    } = useTypedSelector(state => state.feedbackReducer)
    const [selectedCategory, setSelectedCategory] = useState<FeedbackCategoryResource>()
    const [text, setText] = useState('')

    useEffect(() => {
        if (feedbackUuid) {
            dispatch(getFeedbackCategories())
            dispatch(getFeedback(feedbackUuid))
        }
    }, [])

    const handleTextChange = (event: ChangeEvent<HTMLTextAreaElement>): void => {
        setText(event.currentTarget.value)
    }

    const handleSubmitClick = (): void => {
        if (feedbackUuid && selectedCategory) {
            dispatch(submitFeedback(feedbackUuid, text, selectedCategory))
        }
    }

    if (loading || failure) {
        return (
            <div className="feedback">
                <div className="feedback-inner">
                    <h1 className="feedback-title">
                        {failure ? trans('common.error') : `${trans('common.loading')}...`}
                    </h1>
                    {failure ? (
                        <h2 className="feedback-buttons-title">{trans('pages.feedback.getDataFailure')}</h2>
                    ) : (<span />)}
                </div>
            </div>
        )
    }

    return (
        <div className="feedback">
            <div className="feedback-inner">
                <h1 className="feedback-title">
                    {`${name},`}<br />
                    {`${trans('pages.feedback.feedbackComplaintPart1')} ${convertDateToDateYearCompleteString(date)}
                    ${trans('pages.feedback.feedbackComplaintPart2')}
                    ${trans('pages.feedback.wouldYouLikeToTellUseMore')}`}
                </h1>
                <p className="feedback-example">
                    {trans('pages.feedback.giveReason')}
                </p>
                <h2 className="feedback-buttons-title">{trans('pages.feedback.whatCategory')}</h2>
                <form>
                    <div className="feedback-buttons">
                        {categories.map(category => (
                            <button
                                key={category.id}
                                type="button"
                                onClick={() => setSelectedCategory(category)}
                                className={classNames('feedback-button-category', {
                                    'feedback-button-category--active': category.id === selectedCategory?.id,
                                })}
                            >
                                {getCategoryName(category.name)}
                            </button>
                        ))}
                    </div>
                    <textarea
                        onChange={handleTextChange}
                        className="feedback-textarea"
                        rows={6}
                        cols={50}
                        placeholder={trans('pages.feedback.describeYourExperience')}
                    />
                    {/* After a failed submit the button stays, so the feedback can be sent again */}
                    {! dataSubmitSuccess ? (
                        <button
                            className={classNames('btn btn-orange feedback-button-submit', {
                                'feedback-button-submit--sending': sendingData,
                            })}
                            disabled={! (selectedCategory && text)}
                            type="button"
                            onClick={handleSubmitClick}
                        >
                            {trans('pages.feedback.sendFeedback')}
                        </button>
                    ) : (
                        <div>{trans('pages.feedback.thankYouForTheFeedback')}</div>
                    )}
                </form>
            </div>
        </div>
    )
}
